import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles } from "lucide-react";
import { useBrand } from "../lib/BrandContext";

const STORAGE_KEY = "sf_welcome_parent_v1";

/**
 * Parent welcome dialog — appears ONCE on the parent's first visit to Home
 * after accepting a coach invite. We stash the coach + org name in
 * localStorage during invite acceptance and read it here. "Got it" clears
 * the key so the dialog never returns.
 *
 * Deliberately mentions ONLY: coach name, team/business name, what Squad
 * Fee does for the coach. Does NOT mention plans, limits, billing, or
 * subscription details (per spec).
 */
export default function ParentWelcomeDialog() {
  const brand = useBrand();
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let raw;
    try { raw = localStorage.getItem(STORAGE_KEY); } catch { return; }
    if (!raw) return;
    try {
      const p = JSON.parse(raw);
      if (p && p.v === 1) {
        setPayload(p);
        setOpen(true);
      } else {
        // Unknown payload schema — drop it so we don't get stuck.
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const dismiss = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    setOpen(false);
  };

  if (!payload) return null;

  // Title-case "Coach" prefix only when stored coach name doesn't already
  // start with Coach / Trainer / Director (avoids "Coach Coach Maria").
  const coachLabel = payload.coach_name
    ? (/^(Coach|Trainer|Director)\b/i.test(payload.coach_name)
        ? payload.coach_name
        : `Coach ${payload.coach_name}`)
    : null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent data-testid="parent-welcome-dialog" className="max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand text-white">
            <Sparkles className="h-6 w-6" />
          </div>
          <DialogTitle className="text-center text-2xl font-bold">
            Welcome to {brand?.app_name || "Squad Fee"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-center text-sm text-ink-700">
          {(coachLabel || payload.org_name) ? (
            <p data-testid="parent-welcome-invited-by">
              You were invited by{" "}
              {coachLabel ? <span className="font-semibold text-ink-900">{coachLabel}</span> : <span className="font-semibold text-ink-900">your coach</span>}
              {payload.org_name ? (
                <> from <span className="font-semibold text-ink-900">{payload.org_name}</span></>
              ) : null}
              .
            </p>
          ) : null}
          <p>
            {brand?.app_name || "Squad Fee"} helps your coach track fees, payment confirmations, and reminders.
          </p>
        </div>

        <DialogFooter className="sm:justify-center">
          <Button
            onClick={dismiss}
            className="h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover sm:w-auto sm:px-8"
            data-testid="parent-welcome-dismiss"
          >
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
